import React, { useState, useEffect } from 'react';
import Papa from 'papaparse';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ScatterChart, Scatter, ZAxis, Cell, BarChart, Bar } from 'recharts';

const SurveyAnalysisApp = () => {
  // Core state
  const [fileUploaded, setFileUploaded] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sortConfig, setSortConfig] = useState({
    key: 'opportunity',
    direction: 'descending'
  });
  const [processingStats, setProcessingStats] = useState(null);
  const [activeTab, setActiveTab] = useState('table');
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  
  // Filter state
  const [filterQuestions, setFilterQuestions] = useState([]);
  const [activeFilters, setActiveFilters] = useState({});
  const [originalData, setOriginalData] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  // Handle file upload
  const handleFileUpload = async (event) => {
    setLoading(true);
    setError('');
    
    try {
      const file = event.target.files[0];
      if (!file) {
        setLoading(false);
        return;
      }

      console.log("Processing file:", file.name);
      
      const text = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.readAsText(file);
      });

      // Process the data
      processSurveyData(text);
      
      setLoading(false);
    } catch (error) {
      console.error("Error processing file:", error);
      setError(`Error processing file: ${error.message}`);
      setLoading(false);
    }
  };
  
  // Process sample data (simulated for demo)
  const processDemoFile = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Use sample data directly without trying to read a file
      console.log("Using built-in sample data");
      // Sample data that matches the expected format
      const csvText = `Affordable Participation,,Affordable Equipment,,Affordable Training,,Affordable Travel,,Financial Support,,Flexible League Options,,Efficient Gear Packing,,FILTERING QUESTIONS START HERE,Gender,Position,Motivation
Importance: How important is it for you to find budget-friendly ways to participate in organized ?,Satisfaction: How satisfied are you with your current access to budget-friendly ways to play?,Importance: How important is it for you to have affordable  equipment?,Satisfaction: How satisfied are you with the affordability of  equipment?,Importance: How important is it for you that  training is affordable?,Satisfaction: How satisfied are you with the current costs of  training?,Importance: How important is it for you to keep travel costs low?,Satisfaction: How satisfied are you with travel costs?,Importance: How important is it for you to have access to financial support for ?,Satisfaction: How satisfied are you with financial support?,,
5,4,5,3,4,3,5,2,4,3,Male,Forward,Competition
4,3,4,2,5,4,4,3,3,3,Female,Defender,Fitness
5,2,5,2,5,3,5,3,4,3,Male,Forward,Social
3,3,4,4,3,3,4,4,4,4,Non-binary,Goalie,Fun
4,2,5,2,4,2,5,2,5,2,Female,Forward,Competition
`;
      
      processSurveyData(csvText);
      setLoading(false);
    } catch (error) {
      console.error("Error processing demo data:", error);
      setError(`Error processing demo data: ${error.message}`);
      setLoading(false);
    }
  };

  // Request AI analysis of the data
  const requestAiAnalysis = async () => {
    if (!results || results.length === 0) {
      setError("Please analyze survey data before requesting AI insights");
      return;
    }

    setAiLoading(true);
    try {
      // Prepare data to send to Python API
      const dataToSend = {
        results: results,
        demographics: originalData ? originalData.respondentFilters : [],
        questions: filterQuestions
      };

      // This will be replaced with your actual API endpoint
      const response = await fetch('http://localhost:5000/analyze', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dataToSend),
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const analysisResults = await response.json();
      setAiAnalysis(analysisResults);
    } catch (error) {
      console.error("Error getting AI analysis:", error);
      setError(`AI Analysis error: ${error.message}. Is the Python API running?`);
      // For demo purposes, provide mock AI results if API is not available
      setAiAnalysis({
        insights: [
          "Cost-related concerns show the highest opportunity scores, suggesting affordability is a key issue.",
          "Female respondents rate financial support as more important than male respondents.",
          "Competition-motivated players show higher dissatisfaction with travel costs.",
          "Consider developing tiered participation options to address affordability concerns."
        ],
        sentimentByDemographic: {
          "Gender": {
            "Male": 0.65,
            "Female": 0.52,
            "Non-binary": 0.78
          },
          "Motivation": {
            "Competition": 0.55,
            "Fitness": 0.72,
            "Social": 0.68,
            "Fun": 0.85
          }
        }
      });
    } finally {
      setAiLoading(false);
    }
  };
  
  // Process  survey data
  const processSurveyData = (csvText) => {
    try {
      // Split by lines first to handle the specific format
      const lines = csvText.split('\n');
      
      if (lines.length < 3) {
        setError("Invalid CSV format: file needs at least 3 lines");
        return;
      }
      
      // First line is the header row (with topic labels)
      const headerRow = lines[0].split(',');
      
      // Second line contains the actual questions
      const questionRow = lines[1].split(',');
      
      // Find filter marker column
      const filterIndex = headerRow.findIndex(h => 
        h && h.includes && h.includes("FILTERING QUESTIONS START HERE")
      );
      
      if (filterIndex === -1) {
        setError("Could not find 'FILTERING QUESTIONS START HERE' column");
        return;
      }
      
      // Extract filter columns (all non-empty columns after filter marker)
      const filterColumns = [];
      for (let i = filterIndex + 1; i < headerRow.length; i++) {
        if (headerRow[i] && headerRow[i].trim() !== "") {
          filterColumns.push({
            index: i,
            name: headerRow[i].trim()
          });
        }
      }
      
      console.log("Found filter columns:", filterColumns);
      
      // Collect all unique filter values and create respondent-to-filter map
      const filterValues = {};
      const respondentFilters = [];
      
      // Initialize filter values collection
      filterColumns.forEach(col => {
        filterValues[col.name] = new Set();
      });
      
      // Process each data row (starting from line 3)
      for (let rowIndex = 2; rowIndex < lines.length; rowIndex++) {
        if (!lines[rowIndex] || lines[rowIndex].trim() === "") continue;
        
        const cells = lines[rowIndex].split(',');
        const respondentId = rowIndex - 2; // Using row index as respondent ID
        
        // Extract this respondent's filter values
        const filters = {};
        
        filterColumns.forEach(col => {
          if (cells[col.index] && cells[col.index].trim() !== "") {
            const value = cells[col.index].trim();
            filters[col.name] = value;
            filterValues[col.name].add(value);
          }
        });
        
        respondentFilters.push({
          id: respondentId,
          filters
        });
      }
      
      // Convert filter values to sorted arrays
      const filterData = filterColumns.map(col => ({
        question: col.name,
        values: Array.from(filterValues[col.name]).sort()
      }));
      
      // Set filter state
      setFilterQuestions(filterData);
      
      // Initialize all filter values as selected
      const initialFilters = {};
      filterData.forEach(filter => {
        initialFilters[filter.question] = {};
        filter.values.forEach(value => {
          initialFilters[filter.question][value] = true;
        });
      });
      setActiveFilters(initialFilters);
      
      // Find importance/satisfaction question pairs
      const questionPairs = [];
      
      // Create a map to store column titles (from header row 1)
      // Each title will represent the title for both importance and satisfaction questions
      const columnTitles = {};
      
      // First, map column titles to their positions
      for (let i = 0; i < filterIndex; i++) {
        if (headerRow[i] && headerRow[i].trim() !== "") {
          columnTitles[i] = headerRow[i].trim();
        }
      }
      
      // First, try to detect whether we have alternating importance/satisfaction questions
      const importanceQuestions = [];
      const satisfactionQuestions = [];
      
      // Group all importance and satisfaction questions first
      for (let i = 0; i < filterIndex; i++) {
        const questionText = questionRow[i];
        
        if (!questionText) continue;
        
        if (questionText.includes("Importance:") || questionText.includes("important")) {
          importanceQuestions.push({ index: i, text: questionText.replace(/^"/, '').replace(/"$/, '') });
        } else if (questionText.includes("Satisfaction:") || questionText.includes("satisfied")) {
          satisfactionQuestions.push({ index: i, text: questionText.replace(/^"/, '').replace(/"$/, '') });
        }
      }
      
      console.log(`Found ${importanceQuestions.length} importance questions and ${satisfactionQuestions.length} satisfaction questions`);
      
      // FIXED: Matching logic for importance and satisfaction questions
      // Instead of looking for the "closest" question by index, group questions by their topic
      // and match importance/satisfaction within each topic
      
      // Map to store topics and their associated questions
      const topicQuestions = {};
      
      // Find the topic for each column
      for (let i = 0; i < filterIndex; i++) {
        const questionText = questionRow[i];
        if (!questionText) continue;
        
        // Find the topic for this column by looking at nearby column titles
        let topic = null;
        
        // First try the exact column
        if (columnTitles[i]) {
          topic = columnTitles[i];
        } else {
          // Look backwards for a title
          for (let j = i; j >= 0; j--) {
            if (columnTitles[j]) {
              topic = columnTitles[j];
              break;
            }
          }
          
          // If still no title, look forward
          if (!topic) {
            for (let j = i + 1; j < filterIndex; j++) {
              if (columnTitles[j]) {
                topic = columnTitles[j];
                break;
              }
            }
          }
        }
        
        // If we still don't have a topic, use a default
        if (!topic) {
          topic = `Topic ${Object.keys(topicQuestions).length + 1}`;
        }
        
        // Initialize the topic in our map if needed
        if (!topicQuestions[topic]) {
          topicQuestions[topic] = {
            importance: null,
            satisfaction: null
          };
        }
        
        // Add the question to the appropriate category
        if ((questionText.includes("Importance:") || questionText.includes("important")) && !topicQuestions[topic].importance) {
          topicQuestions[topic].importance = { 
            index: i, 
            text: questionText.replace(/^"/, '').replace(/"$/, '') 
          };
        } else if ((questionText.includes("Satisfaction:") || questionText.includes("satisfied")) && !topicQuestions[topic].satisfaction) {
          topicQuestions[topic].satisfaction = { 
            index: i, 
            text: questionText.replace(/^"/, '').replace(/"$/, '') 
          };
        }
      }
      
      // Create question pairs from the topic map
      for (const topic in topicQuestions) {
        if (topicQuestions[topic].importance && topicQuestions[topic].satisfaction) {
          questionPairs.push({
            topic: topic,
            importance: topicQuestions[topic].importance,
            satisfaction: topicQuestions[topic].satisfaction
          });
        }
      }
      
      console.log(`Created ${questionPairs.length} correctly matched question pairs`);
      
      // If the above approach didn't find enough pairs, try an alternative approach
      // This handles cases where the CSV format might not be exactly what we expect
      if (questionPairs.length < 5 && importanceQuestions.length >= 5 && satisfactionQuestions.length >= 5) {
        console.log("Using alternative question pair matching approach");
        questionPairs.length = 0; // Clear existing pairs
        
        // Assume that importance and satisfaction questions are in the same order
        // e.g., first importance question pairs with first satisfaction question, and so on
        const numPairs = Math.min(importanceQuestions.length, satisfactionQuestions.length);
        
        for (let i = 0; i < numPairs; i++) {
          // Find the topic for this pair
          let topic = `Topic ${i + 1}`;
          
          // Try to find a better topic name from column titles
          const importanceIndex = importanceQuestions[i].index;
          
          if (columnTitles[importanceIndex]) {
            topic = columnTitles[importanceIndex];
          } else {
            // Look for nearby column titles
            for (let j = importanceIndex; j >= 0; j--) {
              if (columnTitles[j]) {
                topic = columnTitles[j];
                break;
              }
            }
          }
          
          questionPairs.push({
            topic: topic,
            importance: importanceQuestions[i],
            satisfaction: satisfactionQuestions[i]
          });
        }
        
        console.log(`Alternative approach created ${questionPairs.length} question pairs`);
      }
      
      // Store original data for filtering
      setOriginalData({
        lines,
        headerRow,
        questionRow,
        filterIndex,
        questionPairs,
        respondentFilters,
        filterColumns
      });
      
      // Process with all respondents initially
      const allRespondentIds = respondentFilters.map(r => r.id);
      const processedResults = processWithFilters(
        lines, 
        filterIndex,
        questionPairs,
        allRespondentIds
      );
      
      if (processedResults && processedResults.length > 0) {
        setResults(processedResults);
        setFileUploaded(true);
        
        setProcessingStats({
          format: " Survey",
          totalQuestions: processedResults.length,
          totalRespondents: allRespondentIds.length
        });
      } else {
        setError("Could not process data properly");
      }
      
    } catch (error) {
      console.error("Error processing  survey data:", error);
      setError(`Error: ${error.message}`);
    }
  };
  
  // Process data with filters applied
  const processWithFilters = (lines, filterIndex, questionPairs, includedRespondentIds) => {
    try {
      const results = [];
      
      // Process each question pair
      questionPairs.forEach((pair, pairIndex) => {
        const importanceRatings = [];
        const satisfactionRatings = [];
        
        // Process each respondent's data
        includedRespondentIds.forEach(respondentId => {
          const rowIndex = respondentId + 2; // Convert back to line index
          
          if (rowIndex >= lines.length) return;
          
          const cells = lines[rowIndex].split(',');
          
          // Get importance and satisfaction ratings
          const importanceValue = parseFloat(cells[pair.importance.index]);
          const satisfactionValue = parseFloat(cells[pair.satisfaction.index]);
          
          // Add valid ratings to the collections
          if (!isNaN(importanceValue)) {
            importanceRatings.push(importanceValue);
          }
          
          if (!isNaN(satisfactionValue)) {
            satisfactionRatings.push(satisfactionValue);
          }
        });
        
        // Calculate scores if we have valid ratings
        if (importanceRatings.length > 0 && satisfactionRatings.length > 0) {
          // Count high scores (4-5) for Importance
          const highImportance = importanceRatings.filter(score => score >= 4).length;
          
          // Count high scores (4-5) for Satisfaction
          const highSatisfaction = satisfactionRatings.filter(score => score >= 4).length;
          
          // Calculate percentages and scale to 0-10
          const importanceScore = (highImportance / importanceRatings.length) * 10;
          const satisfactionScore = (highSatisfaction / satisfactionRatings.length) * 10;
          
          // Calculate opportunity score: Importance + (Importance - Satisfaction)
          const opportunity = importanceScore + (importanceScore - satisfactionScore);
          
          // Use the topic directly from the pair as the question title
          const question = pair.topic;
          
          // Add to results
          results.push({
            number: pairIndex + 1,
            question: question,
            fullQuestion: question,
            importance: parseFloat(importanceScore.toFixed(1)),
            satisfaction: parseFloat(satisfactionScore.toFixed(1)),
            opportunity: Math.round(opportunity),
            numRespondents: importanceRatings.length,
            highImportance: highImportance,
            highSatisfaction: highSatisfaction
          });
        }
      });
      
      // Sort by opportunity score (descending)
      return results.sort((a, b) => b.opportunity - a.opportunity);
      
    } catch (error) {
      console.error("Error processing with filters:", error);
      return [];
    }
  };
  
  // Apply selected filters
  const applyFilters = () => {
    if (!originalData) return;
    
    // Filter respondents based on active filters
    const filteredRespondentIds = originalData.respondentFilters
      .filter(respondent => {
        // Check each filter criterion
        for (const filterName in activeFilters) {
          const respondentValue = respondent.filters[filterName];
          
          // Skip this filter if respondent didn't provide an answer
          if (respondentValue === undefined) continue;
          
          // Check if this value is selected in the filter
          if (!activeFilters[filterName][respondentValue]) {
            return false; // Exclude this respondent
          }
        }
        
        return true; // Include this respondent
      })
      .map(r => r.id);
    
    // Process with filtered respondents
    const processedResults = processWithFilters(
      originalData.lines,
      originalData.filterIndex,
      originalData.questionPairs,
      filteredRespondentIds
    );
    
    // Update results and stats
    setResults(processedResults);
    setProcessingStats(prev => ({
      ...prev,
      totalRespondents: filteredRespondentIds.length
    }));
    
    // Clear any previous AI analysis when filters change
    setAiAnalysis(null);
  };
  
  // Handle filter change
  const handleFilterChange = (question, value, isChecked) => {
    setActiveFilters(prev => {
      const updated = { ...prev };
      if (!updated[question]) updated[question] = {};
      updated[question][value] = isChecked;
      return updated;
    });
    
    // Apply filters with a small delay
    setTimeout(applyFilters, 10);
  };
  
  // Toggle all values for a filter
  const toggleAllFilterValues = (question, isChecked) => {
    const filterQuestion = filterQuestions.find(q => q.question === question);
    if (!filterQuestion) return;
    
    setActiveFilters(prev => {
      const updated = { ...prev };
      if (!updated[question]) updated[question] = {};
      
      filterQuestion.values.forEach(value => {
        updated[question][value] = isChecked;
      });
      
      return updated;
    });
    
    // Apply filters
    setTimeout(applyFilters, 10);
  };
  
  // Reset all filters
  const resetFilters = () => {
    const resetFilters = {};
    
    filterQuestions.forEach(filter => {
      resetFilters[filter.question] = {};
      filter.values.forEach(value => {
        resetFilters[filter.question][value] = true;
      });
    });
    
    setActiveFilters(resetFilters);
    
    // Process with all respondents
    if (originalData) {
      const allRespondentIds = originalData.respondentFilters.map(r => r.id);
      const processedResults = processWithFilters(
        originalData.lines,
        originalData.filterIndex,
        originalData.questionPairs,
        allRespondentIds
      );
      
      setResults(processedResults);
      setProcessingStats(prev => ({
        ...prev,
        totalRespondents: allRespondentIds.length
      }));
      
      // Clear any previous AI analysis when filters reset
      setAiAnalysis(null);
    }
  };

  // Function to handle sorting
  const handleSort = (key) => {
    let direction = 'ascending';
    
    // If same key is clicked, toggle direction
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    
    // Create sorted copy of results
    const sortedResults = [...results];
    
    // Sort based on key and direction
    if (key === 'question') {
      sortedResults.sort((a, b) => {
        return direction === 'ascending' 
          ? a.question.localeCompare(b.question)
          : b.question.localeCompare(a.question);
      });
    } else {
      sortedResults.sort((a, b) => {
        return direction === 'ascending' 
          ? a[key] - b[key]
          : b[key] - a[key];
      });
    }
    
    // Update state
    setResults(sortedResults);
    setSortConfig({ key, direction });
  };

  // Get data for the opportunity bar chart
  const getOpportunityBarData = () => {
    return [...results]
      .filter(item => item.opportunity >= 10) // Filter for scores of 10 or above
      .sort((a, b) => b.opportunity - a.opportunity); // Sort by opportunity score (descending)
  };

  // Calculate quadrant statistics
  const getQuadrantStats = () => {
    if (!results.length) return { 
      underserved: 0, 
      overserved: 0, 
      appropriate: 0, 
      lowPriority: 0 
    };
    
    const midpoint = 5; // Using 5 as the midpoint for the quadrants
    
    const underserved = results.filter(item => item.importance >= midpoint && item.satisfaction < midpoint).length;
    const overserved = results.filter(item => item.importance < midpoint && item.satisfaction >= midpoint).length;
    const appropriate = results.filter(item => item.importance >= midpoint && item.satisfaction >= midpoint).length;
    const lowPriority = results.filter(item => item.importance < midpoint && item.satisfaction < midpoint).length;
    
    return { underserved, overserved, appropriate, lowPriority };
  };

  const quadrantStats = getQuadrantStats();

  // Reset app
  const handleReset = () => {
    setFileUploaded(false);
    setResults([]);
    setError('');
    setProcessingStats(null);
    setFilterQuestions([]);
    setActiveFilters({});
    setOriginalData(null);
    setShowFilters(false);
    setAiAnalysis(null);
  };

  // Export results as CSV
  const exportResults = () => {
    if (results.length === 0) return;
    
    // Create CSV content
    const csvContent = Papa.unparse({
      fields: ["Number", "Question", "Importance", "Satisfaction", "Opportunity Score", "Number of Respondents"],
      data: results.map(item => [
        item.number,
        item.fullQuestion || item.question,
        item.importance,
        item.satisfaction,
        item.opportunity,
        item.numRespondents || 0
      ])
    });
    
    // Create download link
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "opportunity_scores.csv");
    link.style.display = "none";
    document.body.appendChild(link);
    
    // Trigger download
    link.click();
    document.body.removeChild(link);
  };
  
  // Filter UI component
  const FilterSection = () => {
    if (filterQuestions.length === 0) return null;
    
    // Count how many filters have been applied (not all values selected)
    let appliedFilterCount = 0;
    
    filterQuestions.forEach(filter => {
      const filterValues = activeFilters[filter.question] || {};
      const allSelected = filter.values.every(value => filterValues[value]);
      if (!allSelected) appliedFilterCount++;
    });
    
    return (
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center text-blue-600 hover:text-blue-800"
          >
            <span className="mr-2">
              {showFilters ? 'Hide Filters' : `Filters ${appliedFilterCount > 0 ? `(${appliedFilterCount} applied)` : ''}`}
            </span>
            <svg className={`w-5 h-5 transition-transform ${showFilters ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
            </svg>
          </button>
          {showFilters && appliedFilterCount > 0 && (
            <button
              onClick={resetFilters}
              className="px-3 py-1 text-sm bg-gray-200 text-gray-700 hover:bg-gray-300 rounded"
            >
              Reset All Filters
            </button>
          )}
        </div>
        
        {showFilters && (
          <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filterQuestions.map((filter, index) => {
                // Check if all values are selected
                const filterValues = activeFilters[filter.question] || {};
                const allSelected = filter.values.every(value => filterValues[value]);
                
                return (
                  <div key={index} className="p-3 bg-white rounded shadow-sm">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="font-medium text-sm">{filter.question}</h4>
                      <div className="flex items-center">
                        <label className="inline-flex items-center text-xs text-gray-600">
                          <input
                            type="checkbox"
                            className="form-checkbox h-3 w-3"
                            checked={allSelected}
                            onChange={(e) => toggleAllFilterValues(filter.question, e.target.checked)}
                          />
                          <span className="ml-1">All</span>
                        </label>
                      </div>
                    </div>
                    <div className="max-h-40 overflow-y-auto px-1">
                      {filter.values.map((value, valueIndex) => (
                        <div key={valueIndex} className="flex items-center mb-1">
                          <label className="inline-flex items-center text-sm w-full hover:bg-gray-50 px-2 py-1 rounded">
                            <input
                              type="checkbox"
                              className="form-checkbox"
                              checked={activeFilters[filter.question]?.[value] ?? true}
                              onChange={(e) => handleFilterChange(filter.question, value, e.target.checked)}
                            />
                            <span className="ml-2 truncate">{value}</span>
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="mt-4 text-sm text-gray-600">
              Showing data from {processingStats ? processingStats.totalRespondents : 0} of {originalData ? originalData.respondentFilters.length : 0} total respondents
            </div>
          </div>
        )}
      </div>
    );
  };

  // AI Analysis Section
  const AiAnalysisSection = () => {
    if (!fileUploaded) return null;
    
    return (
      <div className="mt-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">AI Analysis & Insights</h2>
          <button
            className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:bg-indigo-300"
            onClick={requestAiAnalysis}
            disabled={aiLoading || !results.length}
          >
            {aiLoading ? "Analyzing..." : (aiAnalysis ? "Refresh Analysis" : "Generate AI Insights")}
          </button>
        </div>
        
        {aiLoading && (
          <div className="p-6 bg-gray-50 border border-gray-200 rounded-lg text-center">
            <svg className="animate-spin h-10 w-10 text-indigo-600 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p className="text-gray-600">Analyzing survey data with AI...</p>
          </div>
        )}
        
        {!aiLoading && aiAnalysis && (
          <div className="p-6 bg-white border border-indigo-100 rounded-lg shadow-sm">
            <h3 className="font-semibold text-lg mb-4 text-indigo-800">Key Insights</h3>
            <ul className="mb-6 space-y-2">
              {aiAnalysis.insights.map((insight, index) => (
                <li key={index} className="flex items-start">
                  <span className="inline-flex items-center justify-center bg-indigo-100 text-indigo-800 h-6 w-6 rounded-full flex-shrink-0 mr-2">
                    <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </span>
                  <span className="text-gray-700">{insight}</span>
                </li>
              ))}
            </ul>
            
            {aiAnalysis.sentimentByDemographic && Object.keys(aiAnalysis.sentimentByDemographic).length > 0 && (
              <>
                <h3 className="font-semibold text-lg mb-4 text-indigo-800">Sentiment Analysis by Demographics</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {Object.entries(aiAnalysis.sentimentByDemographic).map(([category, values]) => (
                    <div key={category} className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="font-medium mb-3 text-indigo-700">{category}</h4>
                      <div className="space-y-2">
                        {Object.entries(values).map(([label, score]) => {
                          const percentage = Math.round(score * 100);
                          let barColor = "bg-green-500";
                          if (percentage < 60) barColor = "bg-red-500";
                          else if (percentage < 75) barColor = "bg-yellow-500";
                          
                          return (
                            <div key={label} className="mb-2">
                              <div className="flex justify-between mb-1">
                                <span className="text-sm font-medium text-gray-700">{label}</span>
                                <span className="text-sm font-medium text-gray-700">{percentage}%</span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2.5">
                                <div className={`h-2.5 rounded-full ${barColor}`} style={{ width: `${percentage}%` }}></div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
        
        {error && error.includes("AI Analysis") && (
          <div className="mt-4 p-3 bg-red-100 text-red-700 rounded">
            {error}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="p-6 max-w-6xl mx-auto bg-white">
      <h1 className="text-3xl font-bold mb-6 oklch(13% 0.028 261.692)"> Survey Analyzer</h1>
      
      {!fileUploaded ? (
        <div className="mb-8 p-6 border border-gray-300 rounded-lg bg-gray-50">
          <h2 className="text-xl font-semibold mb-4">Upload Survey Data</h2>
          <p className="mb-4 text-gray-600">
            Upload a CSV file containing your  survey data with importance and satisfaction questions.
          </p>          
          <div className="flex items-center justify-center w-full mb-6">
            <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-blue-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <svg className="w-10 h-10 mb-3 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                </svg>
                <p className="mb-2 text-sm text-gray-500"><span className="font-semibold">Click to upload</span> or drag and drop</p>
                <p className="text-xs text-gray-500">CSV file with  survey data</p>
              </div>
              <input 
                type="file" 
                className="hidden" 
                accept=".csv" 
                onChange={handleFileUpload} 
                disabled={loading}
              />
            </label>
          </div>
          
          <div className="text-center mb-4">
            <p className="text-gray-600 mb-2">- OR -</p>
            <p className="text-gray-600 mb-4">Load the sample data:</p>
            <div className="flex justify-center">
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                onClick={processDemoFile}
                disabled={loading}
              >
                Load Sample  Survey Data
              </button>
            </div>
          </div>
          
          {loading && <p className="mt-4 text-blue-600 text-center">Processing your data...</p>}
          {error && !error.includes("AI Analysis") && <p className="mt-4 text-red-600 text-center">{error}</p>}
        </div>
      ) : (
        
        <div>
          <div className="mb-4 p-4 bg-zinc-800 rounded">
            <h3 className="font-semibold text-zinc-50 mb-2">Opportunity Algorithm</h3>
            <p className="text-sm text-stone-50 mb-2">
              <strong>Opportunity Score = Importance + (Importance — Satisfaction)</strong>
            </p>
            <p className="text-sm text-stone-50 mt-2">
              Where "importance" is the percentage of people rating the outcome 4 or 5 (very or extremely important) 
              and "satisfaction" is the percentage of people rating the outcome 4 or 5 (very or extremely satisfied), 
              scaled to a 0-10 range. The max score for an Opportunity Score is 20. Scores of 10 or above are considered underserved needs and opportunities for innovation
            </p>
          </div>
          <div className="flex justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold">Importance vs. Satisfaction Analysis</h2>
              {processingStats && (
                <p className="text-sm text-gray-600">
                  {processingStats.format} • {processingStats.totalQuestions} questions • {processingStats.totalRespondents} respondents
                </p>
              )}
            </div>
            <div className="flex space-x-4">
              <button 
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                onClick={exportResults}
              >
                Export Results
              </button>
              <button 
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                onClick={handleReset}
              >
                Upload New File
              </button>
            </div>
          </div>
          
          {/* Add Filter Section */}
          <FilterSection />
          
          <div className="mb-6 border-b border-gray-200">
            <div className="flex flex-wrap -mb-px">
              <div className="mr-2">
                <button 
                  className={`inline-block p-4 ${activeTab === 'table' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                  onClick={() => setActiveTab('table')}
                >
                  Results Table
                </button>
              </div>
              <div className="mr-2">
                <button 
                  className={`inline-block p-4 ${activeTab === 'quadrant' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                  onClick={() => setActiveTab('quadrant')}
                >
                  Quadrant Analysis
                </button>
              </div>
              <div className="mr-2">
                <button 
                  className={`inline-block p-4 ${activeTab === 'opportunity' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                  onClick={() => setActiveTab('opportunity')}
                >
                  Opportunity Scores
                </button>
              </div>
            </div>
          </div>
          
          {activeTab === 'table' && (
            <div className="border rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('number')}
                    >
                      #
                      <span className="ml-1">
                        {sortConfig.key === 'number' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
                      </span>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('question')}
                    >
                      Question
                      <span className="ml-1">
                        {sortConfig.key === 'question' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
                      </span>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 bg-red-100"
                      onClick={() => handleSort('importance')}
                    >
                      Importance
                      <span className="ml-1">
                        {sortConfig.key === 'importance' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
                      </span>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 bg-yellow-100"
                      onClick={() => handleSort('satisfaction')}
                    >
                      Satisfaction
                      <span className="ml-1">
                        {sortConfig.key === 'satisfaction' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
                      </span>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 bg-yellow-100"
                      onClick={() => handleSort('opportunity')}
                    >
                      Opportunity Score
                      <span className="ml-1">
                        {sortConfig.key === 'opportunity' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
                      </span>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('numRespondents')}
                    >
                      Respondents
                      <span className="ml-1">
                        {sortConfig.key === 'numRespondents' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {results.length > 0 ? (
                    results.map((item) => (
                      <tr key={item.number} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.number}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" title={item.fullQuestion || item.question}>
                          {item.question}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.importance}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.satisfaction}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium bg-yellow-50">{item.opportunity}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.numRespondents}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                        No data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
          
          {activeTab === 'quadrant' && (
            <div className="grid grid-cols-1 gap-6">
              <div className="border rounded-lg p-4 bg-white shadow">
                <h3 className="text-lg font-medium mb-4">Opportunity Analysis (Importance vs. Satisfaction)</h3>
                {results.length > 0 ? (
                  <div className="h-96">
                    <ResponsiveContainer width="100%" height="100%">
                      <ScatterChart
                        margin={{ top: 20, right: 30, bottom: 60, left: 40 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          type="number" 
                          dataKey="importance" 
                          name="Importance" 
                          domain={[0, 10]} 
                          label={{ value: 'Importance', position: 'insideBottom', offset: -10 }} 
                        />
                        <YAxis 
                          type="number" 
                          dataKey="satisfaction" 
                          name="Satisfaction" 
                          domain={[0, 10]} 
                          label={{ value: 'Satisfaction', angle: -90, position: 'insideLeft' }} 
                        />
                        <ZAxis type="number" dataKey="opportunity" range={[50, 300]} />
                        
                        <Tooltip 
                          cursor={{ strokeDasharray: '3 3' }}
                          content={({ active, payload }) => {
                            if (active && payload && payload.length) {
                              const item = payload[0].payload;
                              return (
                                <div className="p-3 bg-white border rounded shadow">
                                  <p className="font-medium mb-1 text-purple-800">{item.question}</p>
                                  <div className="grid grid-cols-2 gap-2">
                                    <div className="bg-red-50 p-1 rounded">
                                      <p className="text-xs text-gray-600">Importance</p>
                                      <p className="font-bold text-gray-800">{item.importance.toFixed(1)} <span className="text-xs">(n={item.numRespondents || 0})</span></p>
                                    </div>
                                    <div className="bg-yellow-50 p-1 rounded">
                                      <p className="text-xs text-gray-600">Satisfaction</p>
                                      <p className="font-bold text-gray-800">{item.satisfaction.toFixed(1)} <span className="text-xs">(n={item.numRespondents || 0})</span></p>
                                    </div>
                                    <div className="col-span-2 bg-purple-50 p-1 rounded">
                                      <p className="text-xs text-gray-600">Opportunity Score</p>
                                      <p className="font-bold text-gray-800">{item.opportunity} = {item.importance}+({item.importance}-{item.satisfaction})</p>
                                    </div>
                                  </div>
                                </div>
                              );
                            }
                            return null;
                          }}
                        />
                        <Scatter 
                          name="Outcomes" 
                          data={results}
                          fill="oklch(54.6% 0.245 262.881)"
                          opacity={0.8}ç
                        />
                      </ScatterChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-96 flex items-center justify-center text-gray-500">
                    No data available for visualization
                  </div>
                )}
                
                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-3 bg-gray-50 rounded">
                    <h4 className="font-medium text-sm text-zinc-950 mb-2">Opportunity Algorithm</h4>
                    <p className="text-xs text-gray-700 mb-2">
                      Opportunity Score = Importance + (Importance − Satisfaction)
                    </p>
                    <ul className="text-xs text-gray-700 list-disc pl-4 space-y-1">
                      <li>Higher scores indicate greater innovation opportunities</li>
                      <li>The visualization helps identify which outcomes are underserved or overserved</li>
                      <li>Each dot represents a customer need (outcome) from your survey</li>
                    </ul>
                  </div>
                  
                  <div className="p-3 bg-gray-50 rounded">
                    <h4 className="font-medium text-sm text-gray-800 mb-2">Quadrant Analysis Interpretation</h4>
                    <ul className="text-xs text-gray-700 list-disc pl-4 space-y-1">
                      <li><span className="font-medium text-red-600">Underserved Area:</span> High importance, low satisfaction - target for value creation</li>
                      <li><span className="font-medium text-green-600">Overserved Area:</span> Low importance, high satisfaction - target for cost reduction</li>
                      <li><span className="font-medium text-gray-600">Appropriate Area:</span> High importance, high satisfaction - maintain performance</li>
                      <li><span className="font-medium text-yellow-600">Low Priority Area:</span> Low importance, low satisfaction - minimal focus needed</li>
                    </ul>
                  </div>
                </div>

                <div className="mt-4 p-3 bg-blue-50 rounded">
                  <h4 className="font-medium text-sm text-blue-800 mb-2">Quadrant Statistics</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="p-2 bg-red-100 rounded text-center">
                      <p className="text-xs text-gray-600">Underserved</p>
                      <p className="font-bold text-lg text-red-800">
                        {results.filter(item => item.importance >= 5 && item.satisfaction < 5).length}
                      </p>
                      <p className="text-xs text-gray-600">opportunities</p>
                    </div>
                    <div className="p-2 bg-green-100 rounded text-center">
                      <p className="text-xs text-gray-600">Overserved</p>
                      <p className="font-bold text-lg text-green-800">
                        {results.filter(item => item.importance < 5 && item.satisfaction >= 5).length}
                      </p>
                      <p className="text-xs text-gray-600">outcomes</p>
                    </div>
                    <div className="p-2 bg-gray-100 rounded text-center">
                      <p className="text-xs text-gray-600">Appropriate</p>
                      <p className="font-bold text-lg text-gray-800">
                        {results.filter(item => item.importance >= 5 && item.satisfaction >= 5).length}
                      </p>
                      <p className="text-xs text-gray-600">outcomes</p>
                    </div>
                    <div className="p-2 bg-yellow-100 rounded text-center">
                      <p className="text-xs text-gray-600">Low Priority</p>
                      <p className="font-bold text-lg text-yellow-800">
                        {results.filter(item => item.importance < 5 && item.satisfaction < 5).length}
                      </p>
                      <p className="text-xs text-gray-600">outcomes</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {activeTab === 'opportunity' && (
            <div className="grid grid-cols-1 gap-6">
              <div className="border rounded-lg p-4 bg-white shadow">
                <h3 className="text-lg font-medium mb-4">Top Opportunity Scores (10+)</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Showing only items with opportunity scores of 10 or higher - these represent the highest potential for improvement, calculated as: 
                  <span className="font-semibold"> Importance + (Importance - Satisfaction)</span>
                </p>
                
                {results.length > 0 ? (
                  getOpportunityBarData().length > 0 ? (
                    <div>
                      {/* Dynamic height calculation based on number of items - min 400px, 80px per item */}
                      <div style={{ height: `${Math.max(400, getOpportunityBarData().length * 80)}px` }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={getOpportunityBarData()}
                            layout="vertical"
                            margin={{ top: 5, right: 30, left: 150, bottom: 5 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" />
                            <YAxis 
                              type="category" 
                              dataKey="question" 
                              tick={{ fontSize: 12 }}
                              width={250}
                              interval={0} 
                            />
                            <Tooltip formatter={(value, name) => {
                              if (name === 'opportunity') return [`Opportunity Score: ${value}`, 'Opportunity'];
                              if (name === 'importance') return [`Importance: ${value}`, 'Importance'];
                              if (name === 'satisfaction') return [`Satisfaction: ${value}`, 'Satisfaction'];
                              return [value, name];
                            }}/>
                            <Legend />
                            <Bar dataKey="opportunity" fill="#8884d8" name="Opportunity" />
                            <Bar dataKey="importance" fill="#82ca9d" name="Importance" />
                            <Bar dataKey="satisfaction" fill="#ffc658" name="Satisfaction" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="mt-4 p-3 bg-gray-50 rounded">
                        <p className="text-sm text-gray-600">
                          {getOpportunityBarData().length} opportunities with scores of 10 or above found.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="h-96 flex items-center justify-center text-gray-500">
                      No items with opportunity scores of 10 or above found. Consider lowering the threshold to see more results.
                    </div>
                  )
                ) : (
                  <div className="h-96 flex items-center justify-center text-gray-500">
                    No data available for visualization
                  </div>
                )}
              </div>
            </div>
          )}
          
          {error && !error.includes("AI Analysis") && (
            <div className="mt-4 p-3 bg-red-100 text-red-700 rounded">
              {error}
            </div>
          )}
          
          {/* Add AI Analysis Section */}
          <AiAnalysisSection />
          
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded">
            <h3 className="font-semibold text-blue-800 mb-2">About the Opportunity Scores</h3>
            <p className="text-sm text-blue-700 mb-2">
              This analysis uses the standard Opportunity Algorithm to quantify and prioritize customer needs:
            </p>
            <div className="p-3 bg-white rounded mb-3">
              <p className="text-sm text-blue-800 font-bold">
                Opportunity Score = Importance + (Importance — Satisfaction)
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="text-sm font-semibold text-blue-700 mb-1">Importance Score</h4>
                <p className="text-xs text-blue-700">
                  Percentage of people rating the outcome very (4) or extremely (5) important on a 1-5 scale, 
                  converted to a 0-10 scale.
                </p>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-blue-700 mb-1">Satisfaction Score</h4>
                <p className="text-xs text-blue-700">
                  Percentage of people rating the outcome very (4) or extremely (5) satisfied on a 1-5 scale, 
                  converted to a 0-10 scale.
                </p>
              </div>
            </div>
            <div className="mt-4 p-3 bg-white rounded">
              <h4 className="text-sm font-semibold text-blue-700 mb-1">Interpretation</h4>
              <ul className="text-xs text-blue-700 list-disc pl-4 space-y-1">
                <li><span className="font-semibold">Underserved Outcomes:</span> High importance but low satisfaction - these represent the best opportunities for innovation and value creation</li>
                <li><span className="font-semibold">Overserved Outcomes:</span> Low importance but high satisfaction - these are areas where you may be overinvesting and could reduce costs</li>
                <li><span className="font-semibold">Higher opportunity scores indicate greater potential for creating customer value and competitive advantage</span></li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SurveyAnalysisApp;